import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList, Heart, MessageSquare, Bell, Settings, LogOut, ShieldCheck, BadgeCheck,
  Trash2, Plus, Pencil, Megaphone, Users, UserPlus, Building2, Shield, User as UserIcon, ChevronRight,
} from "lucide-react";
import { Coin, VipBadge, fmtK, Eur } from "@/lib/points";
import PromoteButton from "@/components/PromoteButton";
import { TvCastButton } from "@/components/TvCast";
import { wheelEligible } from "@/components/ProductWheel";
import { QrCode, Store, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { catMap } from "@/lib/categories";
import { countryFlag } from "@/lib/places";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";

export default function ProfilePage() {
  const nav = useNavigate();
  const { user, setAuthOpen, logout, badges } = useAuth();
  const { confirm } = useConfirm();
  const [mine, setMine] = useState([]);
  const [savedCount, setSavedCount] = useState(0);
  const [friendsCount, setFriendsCount] = useState(0);
  const [reqCount, setReqCount] = useState(0);
  const listingsRef = useRef(null);

  useEffect(() => { if (user === false) setAuthOpen(true); }, [user, setAuthOpen]);
  useEffect(() => {
    if (user && user !== false) {
      api.get("/my/listings").then((r) => setMine(r.data)).catch(() => {});
      api.get("/favorites").then((r) => setSavedCount((r.data || []).length)).catch(() => {});
      api.get("/friends").then((r) => setFriendsCount((r.data || []).length)).catch(() => {});
      api.get("/friends/requests").then((r) => setReqCount((r.data || []).length)).catch(() => {});
    }
  }, [user]);

  if (!user || user === false) return <div className="p-8 text-center text-slate-500">Kyçu për të parë profilin.</div>;

  const del = async (id) => {
    if (!(await confirm({ title: "Fshi shpalljen", message: "Kjo shpallje do të fshihet përfundimisht." }))) return;
    await api.delete(`/listings/${id}`);
    setMine((m) => m.filter((l) => l.id !== id));
    toast.success("U fshi");
  };
  const reqVerify = async (id) => { await api.post(`/listings/${id}/verify-request`); toast.success("Kërkesa për verifikim u dërgua"); };

  const isBiz = user.account_type !== "private";
  const reklamaCount = mine.filter((l) => l.category === "reklame").length;
  const renewListing = async (id) => {
    try { await api.post(`/listings/${id}/renew`); toast.success("Shpallja u vazhdua"); setMine((m) => m.map((x) => (x.id === id ? { ...x, status: "active" } : x))); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nuk u vazhdua"); }
  };
  const canHomeAd = user.account_type === "business" || mine.some((l) => l.category === "reklame" && l.status === "active");
  const scrollListings = () => listingsRef.current?.scrollIntoView({ behavior: "smooth" });
  const soon = () => toast("Së shpejti 🚀");

  const tiles = [
    { icon: UserIcon, label: "Të dhënat e mia", color: "text-blue-600 bg-blue-50", onClick: () => nav("/profil/ndrysho") },
    { icon: Store, label: "Trego një biznes", color: "text-amber-600 bg-amber-50", onClick: () => nav("/trego-biznes") },
    { icon: ClipboardList, label: "Shpalljet e mia", color: "text-red-600 bg-red-50", onClick: scrollListings },
    { icon: Megaphone, label: "Reklamat e mia", color: "text-orange-600 bg-orange-50", onClick: scrollListings },
    { icon: Heart, label: "Të ruajtura", color: "text-rose-600 bg-rose-50", onClick: () => nav("/te-ruajtura") },
    { icon: MessageSquare, label: "Chat", color: "text-sky-600 bg-sky-50", badge: badges?.messages || 0, onClick: () => nav("/mesazhe") },
    { icon: Users, label: "Shoqëria ime", color: "text-emerald-600 bg-emerald-50", onClick: () => nav("/shoqeria") },
    { icon: UserPlus, label: "Kërkesat për shoqëri", color: "text-teal-600 bg-teal-50", badge: reqCount || badges?.friend_requests || 0, onClick: () => nav("/kerkesat") },
    { icon: Bell, label: "Njoftimet", color: "text-amber-600 bg-amber-50", badge: badges?.notifications || 0, onClick: () => nav("/njoftimet") },
    { icon: Shield, label: "Siguria", color: "text-violet-600 bg-violet-50", onClick: () => nav("/siguria") },
    { icon: Settings, label: "Cilësimet", color: "text-slate-600 bg-slate-100", onClick: () => nav("/cilesimet") },
    { icon: LogOut, label: "Dil nga llogaria", color: "text-red-600 bg-red-50", onClick: async () => { await logout(); toast.success("U çkyçe"); nav("/"); } },
  ];

  const stats = [
    { label: "Shoqëri", value: friendsCount },
    { label: "Shpallje", value: mine.length },
    { label: "Reklama", value: reklamaCount },
    { label: "Të ruajtura", value: savedCount },
  ];

  return (
    <div className="gs-fade px-4 pt-4 pb-6" data-testid="profile-page">
      {/* Header card */}
      <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center gap-1 shrink-0">
            {user.member_no != null && <span data-testid="profile-member-no" className="text-[11px] font-extrabold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">#{String(user.member_no).padStart(2, "0")}</span>}
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center text-3xl font-bold overflow-hidden ring-4 ring-emerald-100">
              {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" data-testid="profile-avatar" /> : (user.name?.[0]?.toUpperCase() || "?")}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-1">
              <h1 className="text-xl font-extrabold text-slate-900 break-words leading-tight flex-1 min-w-0" translate="no" data-testid="profile-name">{user.name} {user.surname}</h1>
              {user.verified && <BadgeCheck className="w-5 h-5 text-sky-500 shrink-0" />}
              {(user.vip_until || "") > new Date().toISOString() && <VipBadge testid="profile-vip-badge" />}
            </div>
            {user.nickname && <p className="text-xs text-slate-400 break-words" data-testid="profile-nickname">Të tjerët të shohin si: <b>{user.nickname}</b></p>}
            <p className="text-sm text-slate-500">{countryFlag(user.country)} {user.city || "—"}{user.country ? `, ${user.country}` : ""}</p>
            <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${isBiz ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>
              {isBiz ? "Biznis" : "Privat"}
            </span>
          </div>
          <button data-testid="edit-profile-btn" onClick={() => nav("/profil/ndrysho")} className="shrink-0 flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2">
            <Pencil className="w-3.5 h-3.5" />Ndrysho
          </button>
        </div>
        {user.bio && <p className="text-sm text-slate-600 mt-3">{user.bio}</p>}
        <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t border-slate-100">
          {stats.map((s) => (
            <div key={s.label} className="text-center" data-testid={`stat-${s.label}`}>
              <div className="text-lg font-extrabold text-slate-900">{s.value}</div>
              <div className="text-[11px] text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <button data-testid="profile-points" onClick={() => nav("/qr")} className={`w-full mt-3 flex items-center gap-3 border-2 rounded-2xl p-3 active:scale-[0.98] transition-transform ${Number(user.points) > 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
        <Coin size={44} /><span className="flex-1 text-left text-2xl font-black text-slate-900">{fmtK(user.points)} <span className="text-lg">kredite</span><Eur v={user.points} className="text-slate-600 -mt-0.5" /><span className={`block text-xs font-semibold ${Number(user.points) > 0 ? "text-emerald-700" : "text-rose-600"}`}>GjejKredit · QR-ja ime · Skano</span></span><ChevronRight className="w-5 h-5 text-slate-700" />
      </button>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <button data-testid="profile-buy-credits" onClick={() => nav("/gjejkredit/bli")} className="btn-3d-green flex items-center gap-2 rounded-2xl p-3 mb-1">
          <span className="w-9 h-9 shrink-0 rounded-xl bg-white/20 flex items-center justify-center"><ShoppingCart className="w-5 h-5" /></span><span className="flex-1 text-left font-bold text-sm leading-tight">Bli GjejKredit</span>
        </button>
        <button data-testid="profile-earn-credits" onClick={() => nav("/gjejkredit?tab=how")} className="btn-3d-soft flex items-center gap-2 text-slate-900 rounded-2xl p-3 mb-1">
          <span className="w-9 h-9 shrink-0 rounded-xl bg-emerald-50 flex items-center justify-center"><Coin size={26} /></span><span className="flex-1 text-left font-bold text-sm leading-tight">Si fitoj kredite? · Historia</span>
        </button>
      </div>

      {(user.role === "admin" || user.role === "staff") && (
        <button data-testid="admin-panel-link" onClick={() => nav("/admin")} className="w-full mt-3 flex items-center gap-3 bg-slate-900 text-white rounded-2xl p-4 font-semibold"><ShieldCheck className="w-5 h-5" />Admin Panel</button>
      )}

      {/* Tiles grid */}
      <div className="grid grid-cols-3 gap-2.5 mt-3">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.label} data-testid={`profile-tile-${t.label}`} onClick={t.onClick} className={`relative rounded-2xl border p-3 flex flex-col items-center gap-2 hover:border-emerald-200 hover:-translate-y-0.5 transition-all active:scale-95 ${t.badge > 0 ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-100"}`}>
              {t.badge > 0 && <span className="absolute top-1.5 right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-4 h-4 px-1 flex items-center justify-center">{t.badge}</span>}
              <span className={`w-11 h-11 rounded-full flex items-center justify-center ${t.color}`}><Icon className="w-5 h-5" /></span>
              <span className="text-[11px] font-semibold text-slate-600 text-center leading-tight">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Own listings */}
      <h2 ref={listingsRef} className="font-bold text-slate-800 mt-6 mb-2 scroll-mt-20">Shpalljet e mia</h2>
      <div className="space-y-2">
        {mine.map((l) => {
          const cat = catMap[l.category] || (l.category === "punekerkues" ? { label: "Punëkërkues", text: "text-emerald-600" } : { label: l.category || "Të tjera", text: "text-slate-500" });
          return (
            <div key={l.id} className="flex items-center gap-3 bg-white rounded-2xl p-2.5 border border-slate-100">
              <img src={(l.images && l.images[0]) || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=200"} alt="" className="w-14 h-14 rounded-xl object-cover" onClick={() => nav(l.category === "punekerkues" ? `/punekerkues/${l.id}` : `/shpallje/${l.id}`)} />
              <div className="flex-1 min-w-0" onClick={() => nav(l.category === "punekerkues" ? `/punekerkues/${l.id}` : `/shpallje/${l.id}`)}>
                <div className="flex items-center gap-1"><span className="font-semibold text-sm text-slate-800 truncate">{l.title}</span>{l.verified && <BadgeCheck className="w-4 h-4 text-emerald-500 shrink-0" />}</div>
                <span className={`text-xs ${cat.text} font-medium`}>{cat.label}</span>
                {l.status && l.status !== "active" && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{l.status === "pending" ? "Në pritje" : l.status === "expired" ? "Skaduar" : "Refuzuar"}</span>}
                {l.has_pending_changes && <span data-testid={`my-changes-pending-${l.id}`} className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">Ndryshim në pritje</span>}
              </div>
              {l.status === "expired" && <button data-testid={`renew-listing-${l.id}`} onClick={() => renewListing(l.id)} className="text-xs text-white bg-emerald-500 rounded-full font-bold px-3 h-8">Vazhdo</button>}
              {l.status === "active" && l.category !== "punekerkues" && <PromoteButton listing={l} />}
              {!l.verified && <button data-testid={`request-verify-${l.id}`} onClick={() => reqVerify(l.id)} className="text-xs text-emerald-600 font-semibold px-2">Verifiko</button>}
              {wheelEligible(l.category, l.subcategory) && <TvCastButton listingId={l.id} />}
              <button data-testid={`edit-listing-${l.id}`} onClick={() => nav(`/ndrysho/${l.id}`)} className="text-slate-400 p-2"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`delete-listing-${l.id}`} onClick={() => del(l.id)} className="text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
            </div>
          );
        })}
        {!mine.length && isBiz && (
          <button onClick={() => nav("/publiko")} className="w-full flex items-center justify-center gap-2 bg-emerald-50 text-emerald-700 rounded-2xl py-4 font-semibold"><Plus className="w-4 h-4" />Publiko shpalljen e parë</button>
        )}
        {!mine.length && !isBiz && (
          <p className="text-center text-slate-400 py-4 text-sm">Llogaritë private nuk publikojnë shpallje.</p>
        )}
      </div>
      {canHomeAd && (
        <button data-testid="profile-homead" onClick={() => nav("/gjejkredit/reklame")} className="w-full mt-6 rounded-3xl overflow-hidden text-left bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-5 active:scale-[0.98] transition-transform shadow-lg shadow-emerald-500/20">
          <span className="flex items-center gap-2 font-extrabold text-lg"><Megaphone className="w-6 h-6" />Reklama jote në faqen kryesore</span>
          <span className="block text-sm text-emerald-50 mt-1">Ngarko foton, shkruaj deri në 4 rreshta tekst, zgjidh 30, 60 ose 90 ditë dhe paguaj me GjejKredit.</span>
          <span className="inline-flex items-center gap-1 mt-3 bg-white text-emerald-700 font-bold text-sm px-4 h-9 rounded-full">Krijo reklamën<ChevronRight className="w-4 h-4" /></span>
        </button>
      )}
    </div>
  );
}
